import * as THREE from 'three';
import type { BallState } from './BallState';

export interface ShotDebugTrailConfig {
  readonly maxPoints?: number;
  readonly sampleIntervalMeters?: number;
}

/**
 * Lightweight visual debugger for validating shot shape in the browser.
 * It deliberately lives in simulation/debug territory rather than the course
 * renderer so the rendering pipeline remains course-data driven.
 */
export class ShotDebugTrail {
  readonly object3D: THREE.Object3D;

  private readonly maxPoints: number;
  private readonly sampleIntervalMeters: number;
  private readonly points: THREE.Vector3[] = [];
  private readonly geometry = new THREE.BufferGeometry();
  private readonly line: THREE.Line;
  private lastSample: THREE.Vector3 | null = null;

  constructor(config: ShotDebugTrailConfig = {}) {
    this.maxPoints = config.maxPoints ?? 240;
    this.sampleIntervalMeters = config.sampleIntervalMeters ?? 1.5;

    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });
    this.line = new THREE.Line(this.geometry, material);
    this.line.name = 'ShotDebugTrail';
    this.object3D = this.line;
  }

  reset(): void {
    this.points.length = 0;
    this.lastSample = null;
    this.geometry.setFromPoints(this.points);
  }

  update(state: BallState): void {
    if (state.isResting && this.points.length > 0) return;

    const position = state.position.clone();
    if (this.lastSample && this.lastSample.distanceTo(position) < this.sampleIntervalMeters) return;

    this.points.push(position);
    if (this.points.length > this.maxPoints) this.points.shift();
    this.lastSample = position;
    this.geometry.setFromPoints(this.points);
  }

  dispose(): void {
    this.geometry.dispose();
    const material = this.line.material;
    if (Array.isArray(material)) material.forEach((m) => m.dispose());
    else material.dispose();
  }
}
